import { useEffect, useRef } from "react";
import { NOT_IN_REALTIME_CONTEXT, useRealtimeRegistry, } from "./RealtimeProvider.js";
/**
 * Call `onReload` whenever any of `channels` reports a change.
 *
 * The only hook a page needs, and deliberately the only thing exported: a
 * caller that held a channel object would be holding the transport's own
 * subscription table, which is how one component unmounting stops another
 * receiving.
 *
 * Pass `undefined` to watch nothing — a read that has no id yet, or one the
 * page is not making. Watching nothing holds no subscription at all rather
 * than holding one quietly.
 */
export function useLiveReload(channels, onReload) {
    const registry = useRealtimeRegistry();
    // Held in a ref so a caller may pass a new function every render — which an
    // inline arrow is — without that re-subscribing anything.
    const callback = useRef(onReload);
    callback.current = onReload;
    // Compared by content rather than identity, for the same reason: a caller
    // building the array inline produces a new one on every render, and an
    // effect keyed on it would unsubscribe and re-subscribe each time.
    const key = channels === undefined ? "" : [...channels].sort().join("\n");
    // One handle for the life of the component, reconciled as the set changes.
    // Releasing and re-taking the whole set instead — which is what an effect
    // keyed on the channels would do, since React cleans up before it re-runs —
    // would drop a channel that is in both the old set and the new one.
    const handle = useRef(undefined);
    useEffect(() => {
        if (registry === undefined) {
            return;
        }
        const watcher = registry.watch(() => {
            callback.current();
        });
        handle.current = watcher;
        return () => {
            watcher.stop();
            handle.current = undefined;
        };
    }, [registry]);
    useEffect(() => {
        if (registry === undefined) {
            if (key !== "") {
                console.warn(NOT_IN_REALTIME_CONTEXT);
            }
            return;
        }
        handle.current?.to(key === "" ? [] : key.split("\n"));
    }, [key, registry]);
}
//# sourceMappingURL=useLiveReload.js.map